/**
 * ModelDetailPage — /agents/libraries/models/:slug
 * One model: headline numbers, the runtime policies that fit it, its capabilities and daemon findings, on a
 * DetailPage with URL-synced tabs.
 * Demo example: /profile (tabbed detail with a stat row) and /connected-apps (capability list with status badges).
 */
import { AlertTriangle, CheckCircle2, SlidersHorizontal } from 'lucide-react';
import type React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getModelOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import { DetailPage } from '../../components/page/DetailPage.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import {
  Badge,
  Callout,
  Card,
  EmptyState,
  ItemList,
  QueryBoundary,
  Skeleton,
  StatStrip,
} from '../../components/ui/index.js';
import type { ItemListItem, StatStripCell } from '../../components/ui/index.js';
import { useTabParam } from '../../hooks/useTabParam.js';
import { runtimePoliciesQuery } from '../../services/api/runtime-policy.queries.js';
import { policyPath } from './runtime/PolicyCatalogList.js';
import { POLICIES_PATH } from './runtime/policy-api.js';
import { humanizeEnum } from './runtime/types.js';

const CAPABILITIES = [
  ['supportsReasoning', 'Reasoning'],
  ['supportsTools', 'Tool calling'],
  ['supportsStructuredOutput', 'Structured output'],
  ['supportsVision', 'Vision'],
  ['supportsStreaming', 'Streaming'],
  ['supportsMultimodal', 'Multimodal'],
  ['supportsRealtime', 'Realtime'],
  ['supportsProviderTools', 'Provider tools'],
  ['supportsPromptCaching', 'Prompt caching'],
  ['supportsFiles', 'Files'],
  ['supportsWebSearch', 'Web search'],
] as const;

const TAB_IDS = ['overview', 'capabilities', 'findings'] as const;
type TabId = (typeof TAB_IDS)[number];

/** Policies worth showing on a model: active ones at its cost tier or for the main kinds of work, at most four. */
const SHOWN_POLICIES = 4;
const MAIN_PURPOSES = ['interactive', 'workflow', 'daemon'];

export const ModelDetailPage: React.FC = () => {
  const { slug = '' } = useParams();
  const [tab, setTab] = useTabParam<TabId>(TAB_IDS, 'overview');
  const modelQuery = useQuery({ ...getModelOptions({ path: { slug } }), enabled: slug !== '' });
  const policiesQuery = useQuery(runtimePoliciesQuery());

  return (
    <QueryBoundary query={modelQuery} label="this model">
      {({ data: model }) => {
        const policies = policiesQuery.data ?? [];
        const matching = policies.filter(
          (policy) =>
            policy.status === 'active' &&
            (policy.costTier === model.costTier || MAIN_PURPOSES.includes(policy.purpose)),
        );
        const shownPolicies = (matching.length > 0 ? matching : policies).slice(0, SHOWN_POLICIES);

        const stats: StatStripCell[] = [
          { label: 'Context window', value: model.maxTokens?.toLocaleString() ?? 'Unknown' },
          { label: 'Max output', value: model.maxOutputTokens?.toLocaleString() ?? 'Unknown' },
          { label: 'Cost tier', value: humanizeEnum(model.costTier) },
          { label: 'Retention', value: humanizeEnum(model.dataRetentionTier) },
        ];

        const policyItems: ItemListItem[] = shownPolicies.map((policy) => ({
          id: String(policy.id),
          title: (
            <Link to={policyPath(policy.id)} className="hover:text-accent-text">
              {policy.name}
            </Link>
          ),
          subtitle: `${policy.slug} · ${humanizeEnum(policy.purpose)}`,
          value: `${policy.maxOutputTokens.toLocaleString()} output`,
          trend: `${policy.maxToolCalls} tool calls · ${humanizeEnum(policy.permissionTier)}`,
        }));

        const capabilityItems: ItemListItem[] = CAPABILITIES.map(([key, label]) => ({
          id: key,
          title: label,
          value: model[key] ? (
            <Badge variant="success" icon={<CheckCircle2 size={12} aria-hidden="true" />}>
              Supported
            </Badge>
          ) : (
            <Badge variant="neutral">Not supported</Badge>
          ),
        }));

        return (
          <DetailPage
            title={model.displayName}
            subtitle={`${model.provider} / ${model.modelName}`}
            breadcrumbs={[
              { label: 'Libraries', to: '/agents/libraries' },
              { label: 'Models', to: '/agents/libraries/models' },
            ]}
            badges={
              <Badge variant={model.enabled ? 'success' : 'danger'}>{model.enabled ? 'Enabled' : 'Disabled'}</Badge>
            }
            tabs={[
              { id: 'overview', label: 'Overview' },
              { id: 'capabilities', label: 'Capabilities' },
              { id: 'findings', label: 'Findings', count: model.findings.length },
            ]}
            activeTab={tab}
            onTabChange={setTab}
          >
            {tab === 'overview' && (
              <>
                <StatStrip cells={stats} />
                <Card
                  title="Runtime policies"
                  actions={
                    <Link
                      to={POLICIES_PATH}
                      className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}
                    >
                      <SlidersHorizontal size={14} aria-hidden="true" />
                      Manage
                    </Link>
                  }
                >
                  {policiesQuery.isLoading && <Skeleton height={48} />}
                  {policiesQuery.isError && <Callout tone="danger">Could not load runtime policies.</Callout>}
                  {!policiesQuery.isLoading && !policiesQuery.isError && policyItems.length === 0 && (
                    <p className="text-sm text-muted">No runtime policies found.</p>
                  )}
                  {policyItems.length > 0 && <ItemList items={policyItems} aria-label="Runtime policies" />}
                </Card>
              </>
            )}
            {tab === 'capabilities' && (
              <Card title="Capabilities">
                <ItemList items={capabilityItems} aria-label="Capabilities" />
              </Card>
            )}
            {tab === 'findings' &&
              (model.findings.length === 0 ? (
                <EmptyState
                  icon={CheckCircle2}
                  title="No open findings"
                  description="The daemon has no open model capability findings for this model."
                />
              ) : (
                model.findings.map((finding) => (
                  <Callout
                    key={finding.id}
                    tone="warning"
                    icon={AlertTriangle}
                    title={finding.title}
                    actions={
                      <Badge variant="neutral">
                        {[finding.severity, finding.reviewStatus]
                          .filter((v): v is string => Boolean(v))
                          .map(humanizeEnum)
                          .join(' · ')}
                      </Badge>
                    }
                  >
                    {finding.description && <p>{finding.description}</p>}
                    {finding.recommendation && <p className="mt-2">{finding.recommendation}</p>}
                  </Callout>
                ))
              ))}
          </DetailPage>
        );
      }}
    </QueryBoundary>
  );
};
